// Public + admin server functions for imported events.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { scrapeFirecrawl } from "../firecrawl.server";
import { runAllSources } from "./sync.server";

export type PublicEvent = {
  id: string;
  slug: string;
  title: string;
  short_description: string;
  full_description: string;
  category: string | null;
  genre: string | null;
  tags: string[];
  venue: string | null;
  city: string | null;
  country: string | null;
  lat: number | null;
  lng: number | null;
  timezone: string;
  start_date: string | null;
  end_date: string | null;
  event_image: string | null;
  gallery_images: string[];
  ticket_url: string | null;
  price: string | null;
  price_from: string | null;
  organizer: string | null;
  source_name: string;
  source_url: string;
  featured: boolean;
};

const ListInput = z.object({
  city: z.string().max(80).optional(),
  category: z.string().max(80).optional(),
  genre: z.string().max(80).optional(),
  source: z.string().max(120).optional(),
  q: z.string().max(120).optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  sort: z.enum(["upcoming", "newest", "featured"]).default("upcoming"),
  limit: z.number().int().min(1).max(100).default(50),
  offset: z.number().int().min(0).max(5000).default(0),
});

export const listImportedEvents = createServerFn({ method: "POST" })
  .inputValidator((input) => ListInput.parse(input))
  .handler(async ({ data }) => {
    let q = supabaseAdmin
      .from("imported_events")
      .select(
        "id, slug, title, short_description, full_description, category, genre, tags, venue, city, country, lat, lng, timezone, start_date, end_date, event_image, gallery_images, ticket_url, price, price_from, organizer, source_name, source_url, featured",
      )
      .eq("approved", true)
      .eq("sync_status", "ok")
      .is("archived_at", null)
      // Hide events that have already ended (2h grace for in-progress events)
      .or(
        `end_date.gte.${new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()},and(end_date.is.null,start_date.gte.${new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()})`,
      );

    if (data.city) q = q.ilike("city", data.city);
    if (data.category) q = q.eq("category", data.category);
    if (data.genre) q = q.eq("genre", data.genre);
    if (data.source) q = q.ilike("source_name", `${data.source}%`);
    if (data.q) q = q.ilike("title", `%${data.q}%`);
    if (data.from) q = q.gte("start_date", data.from);
    if (data.to) q = q.lte("start_date", data.to);

    if (data.sort === "upcoming")
      q = q.order("start_date", { ascending: true, nullsFirst: false });
    else if (data.sort === "newest") q = q.order("imported_at", { ascending: false });
    else q = q.order("featured", { ascending: false }).order("start_date", { ascending: true });

    q = q.range(data.offset, data.offset + data.limit - 1);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    let events = (rows ?? []) as PublicEvent[];
    // For "upcoming" (soonest-first): push already-started/in-progress events to the bottom
    // so upcoming events (start_date >= now) appear first ascending by start_date,
    // followed by in-progress events ordered by end_date ascending.
    if (data.sort === "upcoming") {
      const now = Date.now();
      const upcoming: PublicEvent[] = [];
      const inProgress: PublicEvent[] = [];
      for (const e of events) {
        const startTs = e.start_date ? new Date(e.start_date).getTime() : Infinity;
        if (Number.isFinite(startTs) && startTs < now) inProgress.push(e);
        else upcoming.push(e);
      }
      inProgress.sort((a, b) => {
        const ae = a.end_date ? new Date(a.end_date).getTime() : Infinity;
        const be = b.end_date ? new Date(b.end_date).getTime() : Infinity;
        return ae - be;
      });
      events = [...upcoming, ...inProgress];
    }
    return { events };
  });

export const getImportedEventBySlug = createServerFn({ method: "POST" })
  .inputValidator((input) => z.object({ slug: z.string().min(1).max(120) }).parse(input))
  .handler(async ({ data }) => {
    const { data: row, error } = await supabaseAdmin
      .from("imported_events")
      .select(
        "id, slug, title, short_description, full_description, category, genre, tags, venue, city, country, lat, lng, timezone, start_date, end_date, event_image, gallery_images, ticket_url, price, price_from, organizer, source_name, source_url, featured",
      )
      .eq("approved", true)
      .eq("sync_status", "ok")
      .is("archived_at", null)
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { event: (row ?? null) as PublicEvent | null };
  });

// -------- admin --------

async function assertAdmin(supabase: any) {
  const [a, s] = await Promise.all([
    supabase.rpc("current_user_is_admin"),
    supabase.rpc("current_user_is_super_admin"),
  ]);
  if (!a.data && !s.data) throw new Error("Forbidden");
}

/** Best-effort readable identity of the acting admin for audit entries. */
async function actorEmail(context: any): Promise<string | null> {
  const claimed = context?.claims?.email as string | undefined;
  if (claimed) return claimed;
  const { resolveActorEmail } = await import("./moderation-log.server");
  return resolveActorEmail(context?.userId ?? null);
}


export const adminListSources = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase);
    const { data, error } = await supabaseAdmin
      .from("event_sources")
      .select("*")
      .order("name", { ascending: true });
    if (error) throw new Error(error.message);
    return { sources: data ?? [] };
  });

export const adminListSyncLogs = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase);
    const { data, error } = await supabaseAdmin
      .from("event_sync_logs")
      .select("*")
      .order("started_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return { logs: data ?? [] };
  });

export const adminListImportedEvents = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      status: z
        .enum([
          "all",
          "upcoming",
          "past",
          "draft",
          "approved",
          "pending",
          "duplicates",
          "submitted",
        ])
        .default("all"),
      source: z.string().max(120).optional(),
      limit: z.number().int().min(1).max(500).default(100),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase);
    let q = supabaseAdmin
      .from("imported_events")
      .select(
        "id, slug, title, city, category, venue, start_date, end_date, event_image, ticket_url, source_name, source_url, featured, approved, possible_duplicate, archived_at, imported_at, price, price_from, short_description, organizer, submitter_name, submitter_email, submitter_is_organizer, submission_status",
      )
      .order("start_date", { ascending: true, nullsFirst: false })
      .limit(data.limit);
    const graceIso = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
    if (data.status === "upcoming") {
      q = q
        .eq("approved", true)
        .is("archived_at", null)
        .or(
          `end_date.gte.${graceIso},and(end_date.is.null,start_date.gte.${graceIso})`,
        );
    }
    if (data.status === "past") {
      q = q.or(
        `archived_at.not.is.null,end_date.lt.${graceIso},and(end_date.is.null,start_date.lt.${graceIso})`,
      );
    }
    if (data.status === "draft") q = q.eq("approved", false);
    if (data.status === "approved") q = q.eq("approved", true);
    if (data.status === "pending") q = q.eq("approved", false);
    if (data.status === "duplicates") q = q.eq("possible_duplicate", true);
    if (data.status === "submitted") q = q.not("submission_status", "is", null);
    if (data.source) q = q.ilike("source_name", `${data.source}%`);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return { events: rows ?? [] };
  });

export const adminUpdateEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      id: z.string().uuid(),
      note: z.string().max(1000).optional(),
      patch: z
        .object({
          approved: z.boolean().optional(),
          featured: z.boolean().optional(),
          archived_at: z.string().nullable().optional(),
          title: z.string().min(1).max(300).optional(),
          venue: z.string().max(300).nullable().optional(),
          city: z.string().max(120).nullable().optional(),
          start_date: z.string().nullable().optional(),
          end_date: z.string().nullable().optional(),
          ticket_url: z.string().url().nullable().optional(),
          event_image: z.string().url().nullable().optional(),
          price: z.string().max(80).nullable().optional(),
          short_description: z.string().max(500).nullable().optional(),
          full_description: z.string().nullable().optional(),
        })
        .strict(),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase);
    const { data: before } = await supabaseAdmin
      .from("imported_events")
      .select(
        "id, slug, title, approved, featured, venue, city, start_date, end_date, ticket_url, event_image, price, short_description, full_description, submitter_email",
      )
      .eq("id", data.id)
      .maybeSingle();
    // short_description / full_description are NOT NULL in the DB — coerce
    // any null coming from an older client into an empty string.
    const patch = { ...data.patch } as Record<string, unknown>;
    for (const k of ["short_description", "full_description"]) {
      if (k in patch && patch[k] == null) patch[k] = "";
    }
    const { error } = await supabaseAdmin
      .from("imported_events")
      .update(patch as never)
      .eq("id", data.id);
    if (error) throw new Error(error.message);

    // Notify the submitter of public submissions when status/content changes.
    let notified = false;
    if ((before as { submitter_email?: string | null } | null)?.submitter_email) {
      const wasApproved = Boolean((before as { approved?: boolean }).approved);
      const contentKeys = Object.keys(data.patch).filter(
        (k) => k !== "approved" && k !== "featured",
      );
      const kind =
        data.patch.approved === true && !wasApproved
          ? ("approved" as const)
          : data.patch.approved === false && wasApproved
            ? ("rejected" as const)
            : contentKeys.length
              ? ("edited" as const)
              : null;
      if (kind) {
        try {
          const { notifySubmittersByIds } = await import("./submission-email.server");
          await notifySubmittersByIds([data.id], kind, data.note ?? null);
          notified = true;
        } catch (e) {
          console.error("[admin-update-event] notify failed:", e);
        }
      }
    }

    try {
      const mod = await import("./moderation-log.server");
      const row = (before ?? { id: data.id }) as Record<string, unknown>;
      const changes = mod.diffChanges(row, patch);
      if (changes.length) {
        await mod.logEventModeration({
          rows: [
            {
              id: data.id,
              slug: (row["slug"] as string) ?? null,
              title: (row["title"] as string) ?? null,
              submitter_email: (row["submitter_email"] as string) ?? null,
            },
          ],
          action: mod.actionFromChanges(changes),
          actorUserId: context.userId,
          actorEmail: await actorEmail(context),
          note: data.note ?? null,
          changes,
          notified,
        });
      }
    } catch (e) {
      console.error("[admin-update-event] audit failed:", e);
    }
    return { ok: true };
  });

export const adminDeleteEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ id: z.string().uuid(), note: z.string().max(1000).optional() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase);
    const { data: before } = await supabaseAdmin
      .from("imported_events")
      .select("id, slug, title, submitter_email")
      .eq("id", data.id)
      .maybeSingle();
    let notified = false;
    try {
      const { notifySubmittersByIds } = await import("./submission-email.server");
      await notifySubmittersByIds([data.id], "rejected", data.note ?? null);
      notified = true;
    } catch (e) {
      console.error("[admin-delete-event] notify failed:", e);
    }
    const { error } = await supabaseAdmin.from("imported_events").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    try {
      const mod = await import("./moderation-log.server");
      const row = (before ?? { id: data.id }) as Record<string, unknown>;
      await mod.logEventModeration({
        rows: [
          {
            id: data.id,
            slug: (row["slug"] as string) ?? null,
            title: (row["title"] as string) ?? null,
            submitter_email: (row["submitter_email"] as string) ?? null,
          },
        ],
        action: "deleted",
        actorUserId: context.userId,
        actorEmail: await actorEmail(context),
        note: data.note ?? null,
        notified,
      });
    } catch (e) {
      console.error("[admin-delete-event] audit failed:", e);
    }
    return { ok: true };
  });

/** Archive / restore an event without deleting it. */
export const adminArchiveEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ id: z.string().uuid(), archived: z.boolean() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase);
    const { error } = await supabaseAdmin
      .from("imported_events")
      .update({ archived_at: data.archived ? new Date().toISOString() : null })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Copy an event (e.g. a recurring annual event) as an unpublished draft. */
export const adminDuplicateEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase);
    const { data: row, error: readErr } = await supabaseAdmin
      .from("imported_events")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (!row) throw new Error("Event not found");

    const suffix = Math.random().toString(36).slice(2, 8);
    const src = row as Record<string, unknown>;
    delete src["id"];
    delete src["imported_at"];
    delete src["updated_at"];
    const insert = {
      ...src,
      slug: `${String(row.slug).slice(0, 80)}-copy-${suffix}`,
      title: `${row.title} (Copy)`,
      source_url: `${row.source_url}#copy-${suffix}`,
      approved: false,
      featured: false,
      archived_at: null,
      possible_duplicate: false,
    };
    const { data: created, error } = await supabaseAdmin
      .from("imported_events")
      .insert(insert as never)
      .select("id, slug, title")
      .single();
    if (error) throw new Error(error.message);
    return { event: created };
  });

/** List recent moderation actions for the admin audit view. */
export const adminListEventAuditLog = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        eventId: z.string().uuid().optional(),
        limit: z.number().int().min(1).max(200).default(50),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase);
    let q = supabaseAdmin
      .from("event_moderation_log")
      .select(
        "id, event_id, event_slug, event_title, action, actor_email, note, changes, submitter_email, notified, created_at",
      )
      .order("created_at", { ascending: false })
      .limit(data.limit);
    if (data.eventId) q = q.eq("event_id", data.eventId);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return { entries: rows ?? [] };
  });



export const adminToggleSource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ id: z.string().uuid(), enabled: z.boolean() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase);
    const { error } = await supabaseAdmin
      .from("event_sources")
      .update({ enabled: data.enabled })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Create or update an import source (URL, kind and the filter config JSON). */
export const adminUpsertSource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      id: z.string().uuid().optional(),
      name: z.string().min(2).max(160),
      kind: z.enum(["scrape", "rss", "ical", "api", "linkedevents"]),
      url: z.string().url().max(600),
      enabled: z.boolean().default(true),
      config: z.record(z.string(), z.unknown()).default({}),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase);
    const row = {
      name: data.name,
      kind: data.kind,
      url: data.url,
      enabled: data.enabled,
      config: data.config as never,
    };
    if (data.id) {
      const { error } = await supabaseAdmin.from("event_sources").update(row).eq("id", data.id);
      if (error) throw new Error(error.message);
      return { ok: true, id: data.id };
    }
    const { data: created, error } = await supabaseAdmin
      .from("event_sources")
      .insert(row)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: created?.id as string };
  });

export const adminDeleteSource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase);
    const { error } = await supabaseAdmin.from("event_sources").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Bulk approve / hide / delete imported events from the admin queue. */
export const adminBulkUpdateEvents = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      ids: z.array(z.string().uuid()).min(1).max(500),
      action: z.enum(["approve", "hide", "feature", "unfeature", "delete"]),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase);
    const notifyKind =
      data.action === "approve" ? ("approved" as const)
      : data.action === "hide" || data.action === "delete" ? ("rejected" as const)
      : null;
    let notified = false;
    const notify = async (when: "before" | "after") => {
      if (!notifyKind) return;
      if ((data.action === "delete") !== (when === "before")) return;
      try {
        const { notifySubmittersByIds } = await import("./submission-email.server");
        await notifySubmittersByIds(data.ids, notifyKind);
        notified = true;
      } catch (e) {
        console.error("[admin-bulk-events] notify failed:", e);
      }
    };

    const { data: beforeRows } = await supabaseAdmin
      .from("imported_events")
      .select("id, slug, title, approved, featured, submitter_email")
      .in("id", data.ids);

    const audit = async (patch: Record<string, unknown> | null) => {
      try {
        const mod = await import("./moderation-log.server");
        const email = await actorEmail(context);
        const rows = (beforeRows ?? []) as Array<Record<string, unknown>>;
        const action: import("./moderation-log.server").ModerationAction =
          data.action === "approve" ? "approved"
          : data.action === "hide" ? "rejected"
          : data.action === "delete" ? "deleted"
          : data.action === "feature" ? "featured"
          : "unfeatured";
        for (const r of rows) {
          const changes = patch ? mod.diffChanges(r, patch) : [];
          if (patch && !changes.length) continue;
          await mod.logEventModeration({
            rows: [
              {
                id: r["id"] as string,
                slug: (r["slug"] as string) ?? null,
                title: (r["title"] as string) ?? null,
                submitter_email: (r["submitter_email"] as string) ?? null,
              },
            ],
            action,
            actorUserId: context.userId,
            actorEmail: email,
            changes,
            notified,
          });
        }
      } catch (e) {
        console.error("[admin-bulk-events] audit failed:", e);
      }
    };

    if (data.action === "delete") {
      await notify("before");
      const { error } = await supabaseAdmin.from("imported_events").delete().in("id", data.ids);
      if (error) throw new Error(error.message);
      await audit(null);
      return { ok: true, count: data.ids.length };
    }
    const patch =
      data.action === "approve" ? { approved: true }
      : data.action === "hide" ? { approved: false }
      : data.action === "feature" ? { featured: true }
      : { featured: false };
    const { error } = await supabaseAdmin
      .from("imported_events")
      .update(patch as never)
      .in("id", data.ids);
    if (error) throw new Error(error.message);
    await notify("after");
    await audit(patch);
    return { ok: true, count: data.ids.length };
  });




export const adminResync = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ sourceId: z.string().uuid().optional() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase);
    const summary = await runAllSources({ sourceId: data.sourceId });
    return summary;
  });

// -------- Scrape any event URL via Firecrawl --------

const CATEGORY_KEYS = [
  "music",
  "festival",
  "theatre",
  "sports",
  "comedy",
  "exhibition",
  "family",
  "other",
] as const;

function normalizeCategory(input: string | null | undefined): string {
  const v = (input ?? "").toString().toLowerCase().trim();
  if (!v) return "other";
  if (/(concert|gig|music|band|dj|club|techno|rock|pop|jazz|metal|rap|hip ?hop)/.test(v)) return "music";
  if (/(festival|fest)/.test(v)) return "festival";
  if (/(theatre|theater|musical|opera|drama|stage)/.test(v)) return "theatre";
  if (/(sport|hockey|football|basketball|match|league|f1|racing)/.test(v)) return "sports";
  if (/(comedy|stand[- ]?up|humour|humor)/.test(v)) return "comedy";
  if (/(exhibition|museum|gallery|art show|expo)/.test(v)) return "exhibition";
  if (/(family|kids|children|child)/.test(v)) return "family";
  if ((CATEGORY_KEYS as readonly string[]).includes(v)) return v;
  return "other";
}

function slugifyTitle(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "event";
}

function toIsoMaybe(s: string | null | undefined): string | null {
  if (!s) return null;
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString();
  return null;
}

const ScrapedSchema = {
  type: "object",
  properties: {
    title: { type: "string" },
    description: { type: "string" },
    image_url: { type: "string", description: "Hero image URL of the event" },
    start_date: { type: "string", description: "ISO 8601 start date+time if available" },
    end_date: { type: "string", description: "ISO 8601 end date+time if available" },
    venue: { type: "string" },
    city: { type: "string" },
    country: { type: "string" },
    price: { type: "string", description: "Ticket price or price range, e.g. '25€' or '20–45€'" },
    price_from: { type: "string", description: "Lowest ticket price, e.g. '20€'" },
    category: {
      type: "string",
      description:
        "One of: music, festival, theatre, sports, comedy, exhibition, family, other",
    },
    organizer: { type: "string" },
    ticket_url: { type: "string", description: "Direct ticket purchase URL" },
  },
  required: ["title"],
};

export const adminScrapeEventUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ url: z.string().url().max(2000) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase);

    const doc = (await scrapeFirecrawl(data.url, {
      onlyMainContent: true,
      formats: [
        "markdown",
        {
          type: "json",
          schema: ScrapedSchema,
          prompt:
            "Extract event details from this page. Pick the single most relevant hero image. Dates must be ISO 8601 with timezone offset when possible. Category must be one of: music, festival, theatre, sports, comedy, exhibition, family, other.",
        },
      ],
    })) as any;
    const j = doc?.json ?? {};
    const title = (j.title || doc?.metadata?.title || "Untitled event").toString().trim();

    const category = normalizeCategory(j.category);
    const start = toIsoMaybe(j.start_date);
    const end = toIsoMaybe(j.end_date);

    // unique slug
    const baseSlug = slugifyTitle(title);
    let slug = baseSlug;
    let suffix = 1;
    while (true) {
      const { data: clash } = await supabaseAdmin
        .from("imported_events")
        .select("id")
        .eq("slug", slug)
        .maybeSingle();
      if (!clash) break;
      suffix++;
      slug = `${baseSlug}-${suffix}`;
      if (suffix > 50) {
        slug = `${baseSlug}-${Date.now().toString(36)}`;
        break;
      }
    }

    // upsert by source_url so re-scrape updates instead of duplicating
    const { data: existing } = await supabaseAdmin
      .from("imported_events")
      .select("id, approved")
      .eq("source_url", data.url)
      .maybeSingle();

    // Friendly short source name from the URL host (e.g. "lippu.fi", "tiketti.fi")
    let sourceName = "Manual Scrape";
    try {
      sourceName = new URL(data.url).hostname.replace(/^www\./, "").toLowerCase();
    } catch {
      // keep default
    }

    const row = {
      source_name: sourceName,
      source_url: data.url,
      title,
      short_description: (j.description ?? "").toString().slice(0, 240),
      full_description: (j.description ?? doc?.markdown ?? "").toString().slice(0, 8000),
      category,
      tags: [] as string[],
      venue: j.venue ?? null,
      city: j.city ?? null,
      country: j.country ?? "Finland",
      timezone: "Europe/Helsinki",
      start_date: start,
      end_date: end,
      event_image: j.image_url ?? doc?.metadata?.ogImage ?? null,
      gallery_images: [] as string[],
      ticket_url: j.ticket_url ?? data.url,
      organizer: j.organizer ?? null,
      price: j.price ?? null,
      price_from: j.price_from ?? null,
      sync_status: "ok",
      possible_duplicate: false,
      raw_payload: JSON.parse(JSON.stringify(j)) as never,
      updated_at: new Date().toISOString(),
    };

    if (existing) {
      const { error } = await supabaseAdmin
        .from("imported_events")
        .update(row)
        .eq("id", existing.id);
      if (error) throw new Error(error.message);
      return { ok: true, id: existing.id, slug, updated: true, approved: existing.approved };
    }

    const { data: inserted, error } = await supabaseAdmin
      .from("imported_events")
      .insert({ ...row, slug, approved: false })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: inserted!.id, slug, updated: false, approved: false };
  });
